/**
 * POINTS DE REPÈRE — les bâtiments exclusifs en gemmes (ROADMAP 2.3).
 *
 * ══ Pourquoi ils ne cassent PAS le §14 ═══════════════════════════════════════
 *
 * Le §14 interdit à jamais le pay-to-win sur la production, et G§0 le formule en une
 * question : « un joueur qui ne paie jamais obtient-il la même chose, plus lentement ? »
 *
 * **Oui**, et c'est mesuré : le régime permanent est de ~29 gemmes par jour (G§1), sans
 * dépenser un centime. Le plus cher de ces quatre coûte 180 gemmes, soit ≈ 6 jours de
 * jeu gratuit ; le premier en coûte 60, soit ≈ 2 jours. Rien ici n'est réservé à
 * quiconque paie — l'argent achète du TEMPS, ce que la loi 1 autorise explicitement.
 *
 * Deuxième garde, celle du ROADMAP 2.3 : leur production entre dans le calcul du
 * prestige comme celle de n'importe quel bâtiment. L'avance qu'ils donnent est donc
 * rattrapable en jouant, et elle se dissout dans la boucle plutôt que de s'y ajouter.
 *
 * ══ Ce qu'ils produisent, et pourquoi ce n'est PAS un nombre fixe ════════════
 *
 * Un montant fixe aurait deux vies : écrasant la première heure, dérisoire la
 * cinquième. Il aurait donc violé le principe premier du §1 — « aucun plafond n'est un
 * cul-de-sac » — en devenant du poids mort. Or ce qu'on vend ici est un point de repère
 * qu'on garde toute la partie.
 *
 * Leur production vaut donc **un multiple de la production de base du meilleur type
 * ORDINAIRE débloqué**. Trois conséquences, toutes voulues :
 *
 * · le repère grandit avec la ville, sans jamais devenir un multiplicateur du TOTAL —
 *   ce que G§0 refuse en toutes lettres (« jamais un multiplicateur définitif ») ;
 * · il reste « nettement plus fort » qu'un bâtiment ordinaire à tout moment de la
 *   partie, parce qu'il vaut plusieurs exemplaires du meilleur, sans en avoir le coût
 *   croissant ;
 * · il n'ouvre AUCUN palier : il ne débloque rien, ne change aucune règle, et un joueur
 *   qui n'en pose jamais atteint exactement les mêmes ères.
 *
 * ══ Un seul exemplaire de chaque, et il revient à chaque ère ═════════════════
 *
 * Un repère répétable serait un puits à gemmes infini — commode pour le chiffre du
 * ROADMAP 2.7, malhonnête pour la mesure : « autant qu'il en veut » ne dit rien d'un
 * joueur réel (même argument que pour la collection de décorations). Un seul exemplaire
 * de chaque, donc, et le prestige les remet en vente comme il remet tout le reste :
 * chaque ère rouvre 460 gemmes d'objectif.
 *
 * ⚠️ Ces prix changent la capacité de dépense d'une ère, donc le chiffre du ROADMAP 2.7.
 * Il se MESURE dans `tests/gemEconomy.test.ts`, il ne se suppose pas.
 *
 * ══ Les modèles ont été MESURÉS avant d'être choisis (règle 14) ══════════════
 *
 * La spec disait « emprise 2 ». Le pack dit autre chose : les vingt-neuf `SM_Bld_*` sont
 * tous déjà employés, et parmi les 222 modèles libres, **aucun n'atteint deux cases**.
 * Mesuré à la boîte opaque (`npm run measure:sprites`, colonne « case(s) au naturel ») :
 *
 * | Modèle                    | Largeur | Hauteur | Retenu |
 * | ------------------------- | ------- | ------- | ------ |
 * | `SM_Env_Pool_01`          | 1,14    | 1,03    | ✅     |
 * | `SM_Prop_Helipad_01`      | 0,93    | 1,00    | ✅     |
 * | `SM_Prop_Playground_01`   | 0,77    | 1,06    | ✅     |
 * | `SM_Prop_WaterTower_01`   | 0,44    | **1,30**| ✅     |
 * | `SM_Prop_Spa_01`          | 0,76    | 0,81    | —      |
 * | `SM_Prop_BusStop_01`      | 0,54    | 0,90    | —      |
 * | `SM_Prop_SolarPanel_01`   | 0,37    | 0,39    | —      |
 * | `SM_Prop_Billboard_Base_01` | 0,29  | 0,62    | —      |
 * | `SM_Prop_FlagPole_01`     | 0,23    | 0,42    | —      |
 * | `SM_Prop_BasketballHoop_01` | 0,21  | 0,33    | —      |
 *
 * D'où **emprise 1** : la règle 19 fait de l'emprise un plafond, jamais une cible, et
 * poser un modèle de 0,4 case sur deux cases aurait donné le vide que la dalle
 * `tile-concrete` avait déjà échoué à combler. Le château d'eau est retenu malgré sa
 * largeur parce que c'est sa HAUTEUR qui en fait un repère : 1,30 case, la plus grande
 * silhouette des quatre.
 */

import type { IconName } from '@/ui/icons';

/**
 * ⚠️ **Un repère EST un bâtiment**, et cette interface doit rester compatible avec
 * `BuildingDefinition`.
 *
 * Elle n'importe pas le type — l'import serait circulaire, `buildings.ts` lisant ce
 * fichier. La compatibilité est vérifiée là-bas, à la construction de la table de
 * recherche : un champ manquant ne compile pas. C'est ce qui évite d'ouvrir une seconde
 * voie dans le moteur de rendu, qui ne dessine que ce qu'il sait retrouver par son type.
 */
export interface LandmarkDefinition {
  /** Identifiant stable, persisté dans la sauvegarde. Ne jamais le renommer. */
  readonly type: string;
  readonly icon: IconName;
  /** Sprite rendu depuis le pack (`npm run render:sprites`). */
  readonly rendered: string;
  /** Toujours 1 : mesuré, aucun modèle libre n'atteint deux cases (voir l'en-tête). */
  readonly footprint: number;
  /** Toujours 1 : un repère est unique, il n'a pas de variantes à disperser. */
  readonly variants: number;
  readonly skinId: string;
  /**
   * Toujours 0 — un repère ne s'achète JAMAIS en pièces.
   *
   * Il n'apparaît pas dans `BUILDINGS`, donc jamais dans la chaîne de déblocage ni dans
   * la boutique en pièces. Ce zéro est là pour satisfaire la forme, et il est sûr : un
   * prix de zéro ne peut pas se retrouver affiché quelque part, faute d'écran qui le lise.
   */
  readonly baseCost: number;
  /**
   * Toujours 0 — la production d'un repère n'est PAS un nombre fixe.
   *
   * Elle se calcule dans `landmarkProduction()` depuis `productionFactor` : un montant
   * fixe serait écrasant la première heure et dérisoire la cinquième. Ce zéro garantit
   * aussi qu'aucune boucle générique ne la compte deux fois — le bonus de palier de
   * `currentProduction` le fait, et il rend donc zéro pour un repère, qui n'a pas de paliers.
   */
  readonly baseProduction: number;
  /** Sans objet : un repère ne se débloque pas, il s'achète. */
  readonly unlockAfter: number;
  /** Prix, en gemmes. Un seul exemplaire par ère. */
  readonly gemCost: number;
  /**
   * Multiple de la production de base du meilleur type ORDINAIRE débloqué.
   *
   * 2 signifie « vaut deux exemplaires du meilleur bâtiment que tu sais construire ».
   * C'est ce qui le garde fort à toutes les ères sans jamais toucher au total.
   */
  readonly productionFactor: number;
}

/**
 * Quatre repères, du plus accessible au plus cher.
 *
 * L'ordre est celui de la boutique. Le premier est volontairement **sous le prix d'une
 * extension de terrain** (45) doublé : il doit rester la première grosse dépense qu'un
 * joueur envisage, pas un mur.
 */
export const LANDMARKS: readonly LandmarkDefinition[] = [
  {
    type: 'waterTower',
    skinId: 'waterTower-1',
    footprint: 1,
    variants: 1,
    baseCost: 0,
    baseProduction: 0,
    unlockAfter: 0,
    icon: 'lmTower',
    rendered: 'waterTower-t1.png',
    gemCost: 60,
    productionFactor: 2,
  },
  {
    type: 'playground',
    skinId: 'playground-1',
    footprint: 1,
    variants: 1,
    baseCost: 0,
    baseProduction: 0,
    unlockAfter: 0,
    icon: 'lmPlay',
    rendered: 'playground-t1.png',
    gemCost: 90,
    productionFactor: 3,
  },
  {
    type: 'poolClub',
    skinId: 'poolClub-1',
    footprint: 1,
    variants: 1,
    baseCost: 0,
    baseProduction: 0,
    unlockAfter: 0,
    icon: 'lmPool',
    rendered: 'poolClub-t1.png',
    gemCost: 130,
    productionFactor: 4.5,
  },
  {
    type: 'helipad',
    skinId: 'helipad-1',
    footprint: 1,
    variants: 1,
    baseCost: 0,
    baseProduction: 0,
    unlockAfter: 0,
    icon: 'lmPad',
    rendered: 'helipad-t1.png',
    gemCost: 180,
    productionFactor: 6,
  },
];

const BY_TYPE = new Map(LANDMARKS.map((landmark) => [landmark.type, landmark]));

export function landmarkByType(type: string): LandmarkDefinition | undefined {
  return BY_TYPE.get(type);
}

/** Vrai si ce type de bâtiment est un point de repère et non un bâtiment ordinaire. */
export function isLandmark(type: string): boolean {
  return BY_TYPE.has(type);
}

/**
 * Coût total d'une collection complète, en gemmes.
 *
 * **Dérivé, jamais recopié** (règle 15) : `tests/gemEconomy.test.ts` mesure la capacité
 * d'une ère, et une constante écrite à côté se désynchroniserait au premier prix retouché.
 */
export const LANDMARKS_TOTAL_GEMS = LANDMARKS.reduce(
  (total, landmark) => total + landmark.gemCost,
  0,
);
